import React, { useMemo, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { type SortDirectionType } from 'react-virtualized';
import { nameColumnWidth, headersTotalWidth } from '~/shared/constants';
import { Tooltip } from '../Tooltip';
import Column from './Column';
import {
  BaseTable,
  AutoSizer,
  TableWrapper,
  EmptyHeaderCell,
  HeaderCellContainer,
  HeaderCell,
  HeaderFix,
} from './baseTable.styles';
import {
  NoRowsRenderer,
  StackedhorizontalBarRenderer,
  PosNegBarChartRenderer,
  PercentRenderer,
  PosNegPercentRenderer,
  PosNegPercentRendererBG,
  NumberFloatRenderer,
  NumberFloatRendererBG,
  DefaultCellRenderer,
  ButtonRenderer,
  SparklineRenderer,
  LinkRenderer,
} from './cellRenderers';
import { getCellTextAlign, getFirstPlainTextColumnId } from './cellAlignment';

const TooltipHeader = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: var(--search-text-color);
`;

const TooltipDescription = styled.div`
  font-size: 16px;
  font-weight: 400;
  color: var(--search-text-color);
`;

type BaseTableProps = {
  isSubHeader?: boolean;
  size: any;
  data: Record<string, unknown>[];
  dataColors: string[];
  gridHeight: number;
  headerHeight: number;
  sortBy: string;
  defaultSortBy: string;
  sortDirection: SortDirectionType | undefined;
  handleSetSort: any;
  rowCount: number;
  rowGetter: any;
  rowHeight: number;
  onRowClick?: (data: any) => void;
  dataKeys: string[];
  dataKeysDescription: string[];
  dataKeysWidth: any[] | undefined;
  dataKeysColors: any[];
  dataTypes: (string | undefined)[];
  dataAlignments?: (string | undefined)[];
  dataMidPoints?: number[];
  disableSort: boolean | undefined;
  hasRowNodes: boolean;
  firstItems: string[];
  lastItems: string[];
  debouncedSearchValue: string;
  onHeaderClick: any;
  isLoading: boolean;
  emptyStateText: string;
  hasHeaders: boolean | undefined;
  isDoubleHeaderHeight?: boolean;
  isDoubleSubHeaderHeight?: boolean;
  omitHeaders?: string[];
  setHasScroll?: (val: boolean) => void;
  hasScroll?: boolean;
  indexTitle?: string;
  indexTitleWidth?: number;
  hasSubtitle?: boolean;
  columnIds?: string[];
};

const Table = ({
  isSubHeader,
  size,
  data,
  dataColors,
  gridHeight,
  headerHeight,
  sortBy,
  defaultSortBy,
  sortDirection,
  handleSetSort,
  rowCount,
  rowGetter,
  rowHeight = 50,
  onRowClick,
  dataKeys,
  dataKeysDescription,
  dataKeysWidth,
  dataKeysColors,
  dataTypes,
  dataAlignments = [],
  dataMidPoints,
  disableSort,
  hasRowNodes,
  firstItems = [],
  lastItems = [],
  debouncedSearchValue,
  onHeaderClick,
  isLoading,
  emptyStateText,
  hasHeaders,
  isDoubleHeaderHeight,
  isDoubleSubHeaderHeight,
  omitHeaders,
  setHasScroll,
  hasScroll,
  indexTitle,
  indexTitleWidth,
  hasSubtitle = false,
  columnIds,
}: BaseTableProps) => {
  const tableWidth = useMemo(
    () => (size?.width ? size.width : window.innerWidth - 260 - 260 - 100),
    [window.innerWidth, size],
  );

  const zoom = Math.ceil(((window.outerWidth - 10) / window.innerWidth) * 100);

  const indexTitleWidthBase = indexTitleWidth || nameColumnWidth;
  const headersWidth = indexTitleWidth ? indexTitleWidth + 100 : headersTotalWidth;

  const fallbackColumnIds = useMemo(() => dataKeys.map((dk: string, idx: number) => `${dk}_${idx}`), [dataKeys]);

  const columnIdsList = useMemo(() => {
    if (columnIds && columnIds.length === dataKeys.length) {
      return columnIds;
    }
    return fallbackColumnIds;
  }, [columnIds, fallbackColumnIds, dataKeys]);

  const renderableColumns = useMemo(() => {
    const columns: { dataKey: string; columnId: string; originalIndex: number }[] = [];
    dataKeys.forEach((dk: string, idx: number) => {
      if (!dk.includes('_Components')) {
        const id = columnIdsList[idx] ?? `${dk}_${idx}`;
        columns.push({ dataKey: dk, columnId: id, originalIndex: idx });
      }
    });
    return columns;
  }, [columnIdsList, dataKeys]);

  const columnIdsForRendering = useMemo(() => renderableColumns.map(column => column.columnId), [renderableColumns]);

  const firstTextColumnId = useMemo(
    () => getFirstPlainTextColumnId(renderableColumns, dataTypes, indexTitle),
    [renderableColumns, dataTypes, indexTitle],
  );

  const firstItemsSet = useMemo(
    () => new Set((firstItems || []).filter((item: string | undefined) => !!item)),
    [firstItems],
  );

  const lastItemsSet = useMemo(
    () => new Set((lastItems || []).filter((item: string | undefined) => !!item)),
    [lastItems],
  );

  const handleHeaderClick = useCallback(
    (e: any) => {
      if (onHeaderClick) onHeaderClick(e);
    },
    [onHeaderClick],
  );

  const renderers: { [key: string]: any } = {
    StackedhorizontalBar: StackedhorizontalBarRenderer(
      dataKeysColors || dataColors,
      tableWidth,
      hasRowNodes,
      firstItemsSet,
      lastItemsSet,
      columnIdsForRendering,
      hasScroll,
      indexTitleWidth,
    ),
    Sparkline: SparklineRenderer(
      dataKeysColors || dataColors,
      tableWidth,
      hasRowNodes,
      firstItemsSet,
      lastItemsSet,
      columnIdsForRendering,
      hasScroll,
      indexTitleWidth,
    ),
    PosNegBarChart: PosNegBarChartRenderer(
      dataKeysColors || dataColors,
      firstItemsSet,
      lastItemsSet,
      columnIdsForRendering,
      hasScroll,
    ),
    Percent: PercentRenderer(firstItemsSet, lastItemsSet, columnIdsForRendering, hasScroll),
    PosNegPercent: PosNegPercentRenderer(
      dataKeysColors || dataColors,
      firstItemsSet,
      lastItemsSet,
      columnIdsForRendering,
      hasScroll,
      dataMidPoints,
    ),
    PosNegPercentBG: PosNegPercentRendererBG(
      dataKeysColors || dataColors,
      firstItemsSet,
      lastItemsSet,
      columnIdsForRendering,
      hasScroll,
      dataMidPoints,
    ),
    NumberFloat: NumberFloatRenderer(firstItemsSet, lastItemsSet, columnIdsForRendering, hasScroll),
    NumberFloatBG: NumberFloatRendererBG(
      dataKeysColors || dataColors,
      firstItemsSet,
      lastItemsSet,
      columnIdsForRendering,
      hasScroll,
      dataMidPoints,
    ),
    button: ButtonRenderer(
      debouncedSearchValue,
      firstItemsSet,
      lastItemsSet,
      columnIdsForRendering,
      hasScroll,
      onRowClick,
    ),
    link: LinkRenderer(firstItemsSet, lastItemsSet, columnIdsForRendering, hasScroll),
  };

  const resolveRenderer = (type?: string, textAlign?: 'left' | 'center' | 'right') => {
    if (!type) return undefined;
    const trimmed = type.trim();
    const lower = trimmed.toLowerCase();

    // Recreate alignment-aware renderers when BE/FE alignment is not the type default.
    if (lower === 'button') {
      return ButtonRenderer(
        debouncedSearchValue,
        firstItemsSet,
        lastItemsSet,
        columnIdsForRendering,
        hasScroll,
        onRowClick,
        textAlign ?? 'left',
      );
    }
    if (lower === 'numberfloat') {
      return NumberFloatRenderer(firstItemsSet, lastItemsSet, columnIdsForRendering, hasScroll, textAlign ?? 'center');
    }
    if (lower === 'percent') {
      return PercentRenderer(firstItemsSet, lastItemsSet, columnIdsForRendering, hasScroll, textAlign ?? 'center');
    }

    if (renderers[trimmed]) {
      return renderers[trimmed];
    }

    if (renderers[lower]) {
      return renderers[lower];
    }

    return undefined;
  };

  const header: any = [];

  const createHeader = useCallback(() => {
    for (const [key] of Object.entries(data[0])) {
      if (
        key !== 'id' &&
        key !== 'name' &&
        key !== 'order' &&
        !key.includes('Color') &&
        !key.includes('Max') &&
        !key.includes('Min') &&
        !key.includes('Width') &&
        !key.includes('ShowText')
      ) {
        header.push(key);
      }
    }
  }, [data, header]);

  useEffect(() => {
    data.length !== 0 && createHeader();
  }, [data, createHeader]);

  const customHeaderRenderer =
    (
      column: { dataKey: string; columnId: string; originalIndex: number },
      omitHeaders: string[] | undefined,
      bottom: boolean,
    ) =>
    // eslint-disable-next-line react/display-name
    () => {
      const dataKey = column.dataKey;
      const columnId = column.columnId;
      const description = dataKeysDescription?.[column.originalIndex] || '';
      const normalizedKey = dataKey.replace(/_/g, ' ');
      const shouldHide = omitHeaders?.includes(normalizedKey);
      if (dataKey === 'name' || dataKey === 'order' || dataKey === '' || shouldHide)
        return <EmptyHeaderCell $bottom={bottom ? 1 : 0} />;

      const isFirst = typeof column.originalIndex === 'number' ? column.originalIndex > 0 : false;
      const isLast = columnId ? lastItemsSet.has(columnId) : false;
      const titleArr = dataKey.replaceAll('_', ' ').split('\n');

      return (
        <Tooltip
          arrow
          html={
            <>
              <TooltipHeader>{dataKey.replaceAll('_', ' ').replace('\n', ' ')}</TooltipHeader>
              {description && <TooltipDescription>{description}</TooltipDescription>}
            </>
          }
        >
          <HeaderCellContainer
            $isfirst={isFirst ? 1 : 0}
            $islast={isLast ? 1 : 0}
            $hasscroll={hasScroll ? 1 : 0}
            $issubheader={isSubHeader ? 1 : 0}
          >
            {titleArr.map((t: string, i: number) => (
              <HeaderCell key={i}>{t}</HeaderCell>
            ))}
          </HeaderCellContainer>
        </Tooltip>
      );
    };

  const onScrollbarPresenceChange = ({ vertical }: any) => {
    if (vertical) {
      setHasScroll && setHasScroll(true);
    } else {
      setHasScroll && setHasScroll(false);
    }
  };

  return (
    <TableWrapper
      $gridheight={gridHeight}
      $issubheader={isSubHeader ? 1 : 0}
      $isdoubleheaderheight={isDoubleHeaderHeight ? 1 : 0}
      $hasscroll={hasScroll ? 1 : 0}
      $clickableRows={onRowClick && !isSubHeader ? 1 : 0}
      $hasrownodes={hasRowNodes ? 1 : 0}
    >
      <AutoSizer>
        {({ width, height }: { width: number; height: number }) => {
          // TODO: add double height headers - use gridHeight for percision
          const calcHeadersHeight =
            isDoubleHeaderHeight && isDoubleSubHeaderHeight
              ? height - 56
              : (isDoubleHeaderHeight && !isDoubleSubHeaderHeight) || (!isDoubleHeaderHeight && isDoubleSubHeaderHeight)
                ? height - 26
                : height - 26;
          const calcHeight =
            (hasHeaders ? calcHeadersHeight : gridHeight < 3 ? height - 70 : height) - (hasSubtitle ? 40 : 0);

          return (
            <BaseTable
              data={data}
              width={hasScroll ? (isSubHeader ? width - 0.5 : width - 9) : isSubHeader ? width : width - 9}
              height={calcHeight}
              headerHeight={headerHeight}
              sortBy={sortBy}
              sortDirection={sortDirection}
              rowCount={rowCount}
              rowGetter={rowGetter}
              rowHeight={rowHeight}
              noRowsRenderer={() => <NoRowsRenderer isLoading={isLoading} emptyStateText={emptyStateText} />}
              onRowClick={onRowClick}
              onScrollbarPresenceChange={onScrollbarPresenceChange}
            >
              {renderableColumns.map((column, index: number) => {
                const { dataKey: dk, columnId, originalIndex } = column;
                const dataType = dataTypes?.[originalIndex];
                const width = dataKeysWidth
                  ? (tableWidth / (hasRowNodes ? headersWidth : 100)) * Number(dataKeysWidth[originalIndex])
                  : dataType === 'PosNegPercent'
                    ? window.innerWidth < 1920
                      ? 80
                      : 120
                    : dk === 'order'
                      ? 0
                      : dk === 'name'
                        ? window.innerWidth < 1920
                          ? 180
                          : 280
                        : window.innerWidth < 1920
                          ? 500
                          : 425;

                const cellTextAlign = getCellTextAlign(
                  dk,
                  dataType,
                  columnId,
                  firstTextColumnId,
                  indexTitle,
                  dataAlignments?.[originalIndex],
                );
                const renderer = resolveRenderer(dataType, cellTextAlign);

                return Column({
                  indexTitle,
                  description: dataKeysDescription?.[originalIndex],
                  key: `${columnId}-${index}`,
                  disableSort: dataType === 'Sparkline' || disableSort || data.length < 2,
                  width,
                  minWidth: dk === 'order' ? 0 : dk === 'name' ? indexTitleWidthBase : dk === 'Year' ? 40 : 25,
                  dataKey: omitHeaders && omitHeaders.includes(dk.replace('_', ' ')) ? '' : dk,
                  defaultSortBy,
                  centered: dataType === 'PosNegBarChart',
                  cellRenderer:
                    renderer ||
                    DefaultCellRenderer(cellTextAlign, firstItemsSet, lastItemsSet, columnIdsForRendering, hasScroll),
                  handleSetSort,
                  handleHeaderClick,
                  headerRenderer:
                    hasHeaders && !isSubHeader ? customHeaderRenderer(column, omitHeaders, !!isSubHeader) : undefined,
                });
              })}
            </BaseTable>
          );
        }}
      </AutoSizer>
      {hasScroll && !isSubHeader && <HeaderFix $tzoom={zoom} />}
    </TableWrapper>
  );
};

export default Table;
